import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PlusCircle, X } from 'react-feather';
import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsPageContext, ChatContextItem } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { AuraChatContainer } from '@trinity/chat-components/components/BrandInsights/AuraChat.Container.js';
import { ChatContainer } from '@trinity/chat-components/components/BrandInsights/Chat.Container.js';
import {
  ChatContextSelectorVisibleContext,
  ChatHomeButtonVisibleContext,
  ChatViewType,
  ChatViewTypeContext,
  SidePanelExpandedContext,
} from '@trinity/chat-components/components/BrandInsights/context.js';
import { LinkStateContainer } from '@trinity/chat-components/components/BrandInsights/Link.Container.js';
import { ChatInstance } from '@trinity/chat-components/components/Chat/Chat.js';
import {
  ChatInputFeaturesContext,
  ChatLandingCardsVisibleContext,
  ChatPageContextDataContext,
  DispatchPageContextDataContext,
} from '@trinity/chat-components/components/Chat/context.js';
import { ChatHeader } from '@trinity/chat-components/components/Chat.Header/index.js';
import { ChatStateContainer } from '@trinity/chat-components/components/Chat.State/Chat.State.js';
import { ChatPendingQueryContextContainer } from '@trinity/chat-components/components/Chat.State/ChatPendingQueryContextContainer.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context.js';
import { resetContextSubject } from '@trinity/chat-components/components/Chat.State/state.chat-session.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier.js';
import { useAppSelector } from '@/store';
import useSessionStore from '../state/sessionStore';
import { SegmentationRunSelector } from './SegmentationRunSelector';

const HACKATHON_AGENT_IDENTIFIER = 'hackathon-agent';
const MESSAGE_GENERATION_AGENT_IDENTIFIER = 'message-generation-agent';

function buildAgentContext(
  item: ChatContextItem,
  sessionId: string | null,
  segmentIdentifier: string | null,
  segmentProfile: SegmentProfileSummary | null,
  messageGenSessionId?: string | null
): BrandInsightsPageContext.Agent {
  return {
    type: ChatContextType.Agent,
    data: item,
    metadata: {
      ...(sessionId ? { sessionId } : {}),
      ...(segmentIdentifier ? { segmentIdentifier } : {}),
      ...(segmentProfile ? { segmentProfile } : {}),
      ...(messageGenSessionId ? { messageGenSessionId } : {}),
    },
  };
}

type SegmentProfileSummary = {
  name: string;
  size: number;
  pct: number;
  profile_narrative?: string;
  commercial_action?: string;
  archetype_distribution?: Record<string, number>;
  momentum_distribution?: Record<string, number>;
  distinguishing_features?: { name: string; z_score: number }[];
  top_molecules?: { molecule: string; mean_share: number }[];
};

const noop: Dispatch<SetStateAction<boolean>> = () => {};

export function ChatInterfaceNative({
  onClose,
  segmentIdentifier,
  segmentProfile,
  segmentationRunId,
}: {
  onClose?: () => void;
  segmentIdentifier?: string | null;
  segmentProfile?: SegmentProfileSummary | null;
  segmentationRunId?: number | null;
}): React.JSX.Element {
  const brandIdentifier = useActiveBrandIdentifier();
  const apiClient = useChatApiClient();

  const { data: agents } = useQuery<ChatContextItem[]>({
    queryKey: ['chat-contexts', brandIdentifier],
    queryFn: () => apiClient.chat.getChatContexts({ brandIdentifier }).then((res) => res.items),
    enabled: !!brandIdentifier,
  });

  const [messageGenSessionId, setMessageGenSessionId] = useState<string | null>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [segmentationSessionId, setSegmentationSessionId] = useState<string | null>(null);
  const [prefetchedRuns, setPrefetchedRuns] = useState<any[] | null>(null);

  // Find which agent is being used
  const selectedAgentItem = useMemo(() => {
    if (!agents?.length) return null;
    // Check for message generation agent first
    const messageGenAgent = agents.find((a) =>
      a.agent.identifier.startsWith(MESSAGE_GENERATION_AGENT_IDENTIFIER)
    );
    if (messageGenAgent) return messageGenAgent;

    // Fall back to hackathon agent
    return agents.find((a) => a.agent.identifier.startsWith(HACKATHON_AGENT_IDENTIFIER)) ?? null;
  }, [agents]);

  const isMessageGenerationAgent = useMemo(() => {
    const result = selectedAgentItem?.agent.identifier.startsWith(MESSAGE_GENERATION_AGENT_IDENTIFIER);
    console.log('[ChatInterfaceNative] isMessageGenerationAgent:', result, 'identifier:', selectedAgentItem?.agent.identifier);
    return result;
  }, [selectedAgentItem]);

  // Prefetch runs when message-generation-agent mounted without context
  useEffect(() => {
    if (!isMessageGenerationAgent) return;
    if (segmentationRunId || segmentationSessionId) return; // Already have context
    if (prefetchedRuns !== null) return; // Already fetched

    console.log('[ChatInterfaceNative] Prefetching segmentation runs...');
    apiClient.authenticatedClient
      .get<{ runs: any[] }>('/segmentation-runs', {
        params: { status: 'complete' },
      })
      .then((response) => {
        // Filter runs with sessionId, sort by timestamp descending, take 5
        const sorted = response.data.runs
          .filter((r) => r.sessionId)
          .sort((a, b) => {
            const aTime = a.runTimestamp ? new Date(a.runTimestamp).getTime() : 0;
            const bTime = b.runTimestamp ? new Date(b.runTimestamp).getTime() : 0;
            return bTime - aTime;
          })
          .slice(0, 5);
        setPrefetchedRuns(sorted);
        console.log('[ChatInterfaceNative] Prefetched', sorted.length, 'runs');
      })
      .catch((error) => {
        console.error('[ChatInterfaceNative] Failed to prefetch runs:', error);
        setPrefetchedRuns([]); // Set empty to prevent retry
      });
  }, [isMessageGenerationAgent, segmentationRunId, segmentationSessionId, prefetchedRuns, apiClient]);

  // Fetch segmentation run to get sessionId
  useEffect(() => {
    if (!isMessageGenerationAgent || !segmentationRunId) return;
    if (segmentationSessionId) return; // already fetched

    apiClient.authenticatedClient
      .get<{ run: { sessionId: string | null } }>(`/segmentation-runs/${segmentationRunId}`)
      .then((response) => {
        if (response.data.run.sessionId) {
          setSegmentationSessionId(response.data.run.sessionId);
        }
      })
      .catch((error) => {
        console.error('[ChatInterfaceNative] Failed to fetch segmentation run:', error);
      });
  }, [isMessageGenerationAgent, segmentationRunId, segmentationSessionId, apiClient]);

  // Auto-create message generation session when message-generation-agent is selected
  useEffect(() => {
    if (!isMessageGenerationAgent || !segmentationSessionId || !segmentIdentifier) return;
    if (messageGenSessionId || isCreatingSession) return;

    setIsCreatingSession(true);

    // Create message generation session with segment context
    apiClient.authenticatedClient
      .post<{ sessionId: string; chatInstanceId: string }>(
        '/message-gen-sessions/start',
        {
          config: {
            segmentIdentifier,
            segmentationSessionId,
            count: 5,
            category: 'Efficacy',
            targetAudience: 'HCPs',
          },
        }
      )
      .then((response) => {
        setMessageGenSessionId(response.data.sessionId);
        console.log('[ChatInterfaceNative] Created message generation session:', response.data);
      })
      .catch((error) => {
        console.error('[ChatInterfaceNative] Failed to create message generation session:', error);
      })
      .finally(() => {
        setIsCreatingSession(false);
      });
  }, [
    isMessageGenerationAgent,
    segmentationSessionId,
    segmentIdentifier,
    messageGenSessionId,
    isCreatingSession,
    apiClient,
  ]);

  const resolvedAgent = useMemo(() => {
    if (!selectedAgentItem) return null;
    return buildAgentContext(
      selectedAgentItem,
      segmentationSessionId,
      segmentIdentifier ?? null,
      segmentProfile ?? null,
      messageGenSessionId
    );
  }, [selectedAgentItem, segmentationSessionId, segmentIdentifier, segmentProfile, messageGenSessionId]);

  const [contextData, setContextData] = useState<BrandInsightsPageContext.Item | null>(resolvedAgent);
  const [prevResolvedAgent, setPrevResolvedAgent] = useState(resolvedAgent);
  if (resolvedAgent !== prevResolvedAgent) {
    setPrevResolvedAgent(resolvedAgent);
    if (resolvedAgent) {
      setContextData(resolvedAgent);
    }
  }

  console.log('Metadata: ', JSON.stringify(resolvedAgent?.metadata, null, 2));

  const canToggleAuraChat = useAppSelector((s) => s.features.chatAuraIframesEnabled);

  const sidePanelExpandedValue = useMemo<[boolean, Dispatch<SetStateAction<boolean>>]>(
    () => [true, noop],
    []
  );

  const inputFeaturesValue = useMemo(() => ({ fileUpload: false, voiceInput: true }) as const, []);

  // Show run selector if message-generation-agent but no segmentationSessionId
  const showRunSelector = isMessageGenerationAgent && !segmentationSessionId && !segmentationRunId;
  console.log('[ChatInterfaceNative] showRunSelector:', showRunSelector, {
    isMessageGenerationAgent,
    segmentationSessionId,
    segmentationRunId,
  });

  const handleSelectRun = (runId: number, sessionId: string) => {
    // Set the sessionId so the auto-create session effect can trigger
    setSegmentationSessionId(sessionId);
  };

  const handleSelectSegment = (runId: number, sessionId: string, segmentIdentifierFromRun: string) => {
    // Set both sessionId and segment, then auto-create will trigger
    setSegmentationSessionId(sessionId);
    // Note: we can't directly update segmentIdentifier prop, but we can trigger creation
    // with the segment from the run selector
    apiClient.authenticatedClient
      .post<{ sessionId: string; chatInstanceId: string }>(
        '/message-gen-sessions/start',
        {
          config: {
            segmentIdentifier: segmentIdentifierFromRun,
            segmentationSessionId: sessionId,
            count: 5,
            category: 'Efficacy',
            targetAudience: 'HCPs',
          },
        }
      )
      .then((response) => {
        setMessageGenSessionId(response.data.sessionId);
        console.log('[ChatInterfaceNative] Created message generation session from selector:', response.data);
      })
      .catch((error) => {
        console.error('[ChatInterfaceNative] Failed to create message generation session:', error);
      });
  };

  if (showRunSelector) {
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        <PageHeader>
          <StyledAutoAwesomeIcon />
          <PageHeaderTitle>Generate Messages</PageHeaderTitle>
          {onClose && (
            <CloseBtn onClick={onClose} title="Hide chat">
              <X size={16} />
            </CloseBtn>
          )}
        </PageHeader>
        <SegmentationRunSelector
          onSelectRun={handleSelectRun}
          onSelectSegment={handleSelectSegment}
          prefetchedRuns={prefetchedRuns}
        />
      </div>
    );
  }

  if (!contextData) {
    if (resolvedAgent) {
      setContextData(resolvedAgent);
    }
    return null;
  }

  return (
    <AuraChatContainer canToggleAuraChat={canToggleAuraChat}>
      <ChatViewTypeContext.Provider value={ChatViewType.Page}>
        <DispatchPageContextDataContext.Provider value={setContextData}>
          <ChatPageContextDataContext.Provider value={contextData}>
            <ChatStateContainer>
              <ChatPendingQueryContextContainer>
                <LinkStateContainer>
                  <ChatContainer>
                    <ChatInputFeaturesContext.Provider value={inputFeaturesValue}>
                      <ChatLandingCardsVisibleContext.Provider value={false}>
                        <SidePanelExpandedContext.Provider value={sidePanelExpandedValue}>
                          <ChatHomeButtonVisibleContext.Provider value={false}>
                            <ChatContextSelectorVisibleContext.Provider value={false}>
                              <PageHeader>
                                <StyledAutoAwesomeIcon />
                                <PageHeaderTitle>Discover Insights</PageHeaderTitle>
                                {onClose && (
                                  <CloseBtn onClick={onClose} title="Hide chat">
                                    <X size={16} />
                                  </CloseBtn>
                                )}
                              </PageHeader>
                              <HeaderRow>
                                <ChatHeader />
                                <NewChatButton agentContext={contextData} />
                              </HeaderRow>
                              <ChatInstance isSidePanel />
                            </ChatContextSelectorVisibleContext.Provider>
                          </ChatHomeButtonVisibleContext.Provider>
                        </SidePanelExpandedContext.Provider>
                      </ChatLandingCardsVisibleContext.Provider>
                    </ChatInputFeaturesContext.Provider>
                  </ChatContainer>
                </LinkStateContainer>
              </ChatPendingQueryContextContainer>
            </ChatStateContainer>
          </ChatPageContextDataContext.Provider>
        </DispatchPageContextDataContext.Provider>
      </ChatViewTypeContext.Provider>
    </AuraChatContainer>
  );
}

function NewChatButton({ agentContext }: { agentContext: BrandInsightsPageContext.Item }): React.JSX.Element {
  const dispatch = useContext(ChatStateDispatchContext);
  const pageContext = useContext(ChatPageContextDataContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const handleNewChat = useCallback(() => {
    const context = pageContext ?? agentContext;
    const resetLandingCards =
      context.type === ChatContextType.Agent && context.data.showLandingCardsOnNewChat;

    dispatch({
      type: 'new-chat-started',
      payload: {
        context: resetContextSubject(context, resetLandingCards),
        brandIdentifier,
        resetLandingCards,
      },
    });
  }, [dispatch, pageContext, agentContext, brandIdentifier]);

  return (
    <Tooltip title="New Chat">
      <StyledNewChatButton onClick={handleNewChat}>
        <PlusCircle size={20} />
      </StyledNewChatButton>
    </Tooltip>
  );
}

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const PageHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 8px 0',
  marginBottom: '8px',
});

const PageHeaderTitle = styled.span({
  fontSize: 16,
  fontWeight: 600,
  color: '#1a1a1a',
  flex: 1,
});

const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  '& > *:first-child': {
    flex: 1,
    minWidth: 0,
  },
});

const CloseBtn = styled.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  color: theme.palette.gray[400],
  padding: '4px',
  borderRadius: '4px',
  cursor: 'pointer',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.gray[100],
    color: theme.palette.gray[700],
  },
}));

const StyledNewChatButton = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '6px',
  marginRight: '8px',
  borderRadius: '4px',
  color: theme.palette.primary.main,
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,
  },
}));
